import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ContentLayout from '@layouts/ContentLayout';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { clearUserDocs, searchUserDocs } from '@store/actions/workflowActions';
import FullPageSpinner from '@components/FullPageSpinner';
import { Divider, Flex, Grid, Heading, useDisclosure, VStack } from '@chakra-ui/react';
import NronboardOcrDetails from '@components/NrOnboard/OcrDetails';
import { getOcrDetails } from '@store/actions/nronboardActions';
import ModalLayout from '@components/Modal/ModalLayout';
import EditOcrModal from './EditOcrModal';
import { updateOcrByDoc } from '@store/actions/workFlowsActionsV2';
import JsonViewer from '@components/Modal/JsonViewer';
import InputComponent from '@components/Input';
import { MdSearch } from 'react-icons/md';
import TabSection from './TabSection';
import { TAB_STATES } from './utils';
import { updateWorkflowDocsReqData } from '@store/actions/reqDataActions';

const UserWorkflowDocs = () => {
  const { userId } = useParams();
  const {
    isLoading,
    data: docs,
    totalPages
  } = useSelector((state) => state.workflow.docs);
  const searchReqData = useSelector((state) => state.reqData.workflowDocsReq);
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const navigate = useNavigate();
  const initialReferenceId = queryParams.get('referenceId') || "";
  const [searchText, setSearchText] = useState(initialReferenceId);
  //To check whether the docRefType provided in the params exist in the TAB States or set default tab state to be USER
  const paramDocRefType = queryParams.get("docRefType")?.toUpperCase();
  const [initialLoading, setInitialLoading] = useState(true);
  const currentTab = searchReqData.docRefType;
  //For the Is Rejected Modal
  const {
    isOpen: isRejectionModelOpen,
    onOpen: onRejectionModelOpen,
    onClose: onRejectionModelClose
  } = useDisclosure();
  const [currentRejectionReason, setCurrentRejectionReason] = useState('');

  const {
    isOpen: isEditOcrOpen,
    onOpen: onEditOcrOpen,
    onClose: onEditOcrClose
  } = useDisclosure();

  const dispatch = useDispatch();
  const initialMount = useRef(true);
  const searchTimer = useRef(null);

  const handlePageChange = useCallback(
    (newPageNo) => {
      const pageIndex = newPageNo - 1;
      if (pageIndex !== searchReqData.pageNo) {
        dispatch(updateWorkflowDocsReqData({
          ...searchReqData,
          pageNo: pageIndex
        }));

      }
    },
    [dispatch, searchReqData]
  );
  //This useEffect will only trigger on the first render/mount
  useEffect(() => {
    if (!userId) return;

    const urlDocRefType = Object.values(TAB_STATES).includes(paramDocRefType)
      ? paramDocRefType
      : TAB_STATES.USER;

    dispatch(updateWorkflowDocsReqData({
      ...searchReqData,
      docRefType: urlDocRefType,
      referenceId: initialReferenceId,
      pageNo: 0,
    }));

  }, [userId]);
  // This will run every time a change is made in search Text, Tabs are switched or page no is changed
  useEffect(() => {
    if (!userId || !searchReqData.docRefType) return;
    dispatch(searchUserDocs({
      userId,
      searchReq: {
        referenceId: searchReqData.referenceId,
        docRefType: searchReqData.docRefType,
        pageNo: searchReqData.pageNo,
        pageSize: searchReqData.pageSize || 10
      },
      onComplete: () => setInitialLoading(false)
    }));
  }, [userId, searchReqData]);
  //Automatically switches the tab on the basis of docRefType of the first document Fetched
  useEffect(()=> {
    if(Array.isArray(docs) && docs.length > 0) {
      const firstDocRefType = docs[0].documentRefType?.toUpperCase();
      const isValidTab = Object.values(TAB_STATES).includes(firstDocRefType);
      if(isValidTab && firstDocRefType !== currentTab) {
        dispatch(updateWorkflowDocsReqData({
          ...searchReqData,
          docRefType: firstDocRefType,
          pageNo:0
        }));
        navigate(`?referenceId=${searchText}&docRefType=${firstDocRefType}`);
      }
    }
  },[docs]);
  const handleSearchTextChange = (e) => {
    e.preventDefault();
    const value = e.target.value;
    setSearchText(value);
    clearTimeout(searchTimer.current); // Clear any previous timeout
    searchTimer.current = setTimeout(() => {
      // Dispatch the action to update search results in Redux
      navigate(`?referenceId=${value}&docRefType=${currentTab}`);
      dispatch(updateWorkflowDocsReqData({
        ...searchReqData,
        referenceId: value,
        docRefType: currentTab,
        pageNo: 0
      }));
    }, 1000);
  };

  const handleDocRefTypeChange = (index) => {
    const newTab = Object.values(TAB_STATES)[index];
    dispatch(updateWorkflowDocsReqData({ ...searchReqData, docRefType: newTab.toUpperCase(), pageNo: 0 }));
    navigate(`?referenceId=${searchText}&docRefType=${newTab.toUpperCase()}`);
  }

  /* For OCR Data */
  const [ocrData, setOcrData] = useState({});
  const [isOcrLoading, setOcrLoading] = useState(false);

  const [selectedOcrKey, setSelectedOcrKey] = useState(null);

  useEffect(() => {
    fetchOcrData();
    return () => dispatch(clearUserDocs());
  }, []);

  const fetchOcrData = () => {
    setOcrLoading(true);
    dispatch(
      getOcrDetails({
        userId,
        isNronboarding: false,
        onSuccess: (data) => {
          setOcrData(data?.ocrData);
        },
        onComplete: () => {
          setOcrLoading(false);
        }
      })
    );
  };
  if (initialLoading) return <FullPageSpinner />;
  const handleEditOcrClick = (docKey) => {
    if (!docKey) return;
    setSelectedOcrKey(docKey);
    onEditOcrOpen && onEditOcrOpen();
  };

  const handleEditOcrSubmit = (data) => {
    setOcrLoading(true);
    dispatch(
      updateOcrByDoc({
        userId,
        payload: {
          ...data
        },
        onSuccess: () => {
          // TODO: fetch OCR data here
          fetchOcrData();
        },
        onComplete: () => {
          setOcrLoading(false);
          handleEditOcrCancel();
        }
      })
    );
  };

  const handleEditOcrCancel = () => {
    setSelectedOcrKey(null);
    onEditOcrClose && onEditOcrClose();
  };

  // To handle viewing the Rejection Reason modal
  const handleViewRejectionReason = (reason) => {
    setCurrentRejectionReason(reason);
    onRejectionModelOpen();
  }
  return (
    <>
      <ModalLayout isOpen={isEditOcrOpen} onClose={handleEditOcrCancel}>
        <EditOcrModal
          ocrData={ocrData}
          subDocumentType={selectedOcrKey}
          onCancel={handleEditOcrCancel}
          onSubmit={handleEditOcrSubmit}
        />
      </ModalLayout>
      <ModalLayout isOpen={isRejectionModelOpen} onClose={onRejectionModelClose}>
        <JsonViewer
          title={'Rejection Reason'}
          subTitle={''}
          jsonString={currentRejectionReason}
        />
      </ModalLayout>
      <ContentLayout>
        <Grid gridTemplateColumns={{ base: '1fr', lg: '7fr 0fr 2fr' }}>
          <VStack
            p={10}
            gap={4}
            overflowY={{ base: 'initial', lg: 'scroll' }}
            overflowX={'hidden'}
            h={{ lg: '90vh' }}
            px={{ base: 4, sm: 10 }}
            alignItems={'stretch'}
            className="scroll"
          >
            <Flex
              w={'100%'}
              alignItems={'baseline'}
              justifyContent={'space-between'}
              pb={2}
            >
              <Heading color={'colorPrimary'}>User workflow docs</Heading>
            </Flex>

            <InputComponent
              type="text"
              value={searchText}
              name="search"
              handleChange={handleSearchTextChange}
              placeholder="Search by userId and referenceId"
              inputTitle="Search users"
              leftElement={<MdSearch size="2rem" color="white" />}
              width="40rem"
              maxWidth="100%"
              autoComplete={'off'}
              spellcheck={false}
              autoCorrect={'off'}
            />

            <TabSection
              selectedTab={currentTab}
              onTabChange={handleDocRefTypeChange}
              docs={docs}
              totalPages={totalPages}
              currentPage={searchReqData.pageNo + 1}
              onPageChange={handlePageChange}
              isLoading={isLoading}
              isError={docs?.isError}
              error={docs?.error}
              onViewRejectionReason={handleViewRejectionReason}
            />

            {searchReqData.docRefType === 'USER' && <NronboardOcrDetails
              ocrDetails={ocrData}
              isOnboarding={false}
              isLoading={isOcrLoading}
              handleEditClick={handleEditOcrClick}
            />}

          </VStack>
          <Divider
            style={{ margin: '0' }}
            borderColor="whiteAlpha.300"
            orientation="vertical"
          ></Divider>
        </Grid>
      </ContentLayout>
    </>
  );
};

export default UserWorkflowDocs;

